/**
 * Cashiers.jsx — Phase 25D
 *
 * Operational-staff management surface for school_admin. Lets the owner/admin
 * onboard and offboard counter staff who:
 *   - Collect fees (POST /fee-payments)
 *   - Look up student dues / receipts
 *   - Run the Monthly Verification + Defaulters operational reports
 *
 * Strict least-privilege is enforced by Phase 25C — there is no UI here for
 * granting any of those permissions; creating a cashier IS the permission.
 *
 * Deactivation (toggle-status) is the offboarding path. Hard delete is not
 * exposed because cashiers who collected receipts must persist for audit
 * (`collected_by_id` references on fee_payments).
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Plus,
  MagnifyingGlass,
  DotsThreeVertical,
  Power,
  Key,
  PencilSimple,
  Receipt,
} from "@phosphor-icons/react";
import api, { cleanPayload, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const CREATE_EMPTY = { name: "", email: "", phone: "", password: "" };
const EDIT_EMPTY = { name: "", phone: "" };

function CreateCashierDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(CREATE_EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setForm(CREATE_EMPTY);
      setError("");
    }
  }, [open]);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/school/cashiers", cleanPayload(form));
      toast.success(`Cashier ${data.name} added`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err));
    }
    setSubmitting(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-cashier-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Add cashier</DialogTitle>
          <DialogDescription>
            Creates a counter-staff account. They can collect fees, look up
            students, and run operational reports — but cannot see school-wide
            financial totals or analytics.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Full name *</Label>
            <Input required value={form.name} onChange={update("name")} className="mt-1.5" data-testid="cashier-name-input" />
          </div>
          <div>
            <Label>Email *</Label>
            <Input required type="email" value={form.email} onChange={update("email")} className="mt-1.5" data-testid="cashier-email-input" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={update("phone")} className="mt-1.5" placeholder="+91-…" data-testid="cashier-phone-input" />
          </div>
          <div>
            <Label>Initial password *</Label>
            <Input
              required
              type="text"
              minLength={8}
              value={form.password}
              onChange={update("password")}
              className="mt-1.5 font-mono"
              data-testid="cashier-password-input"
              placeholder="Min 8 characters"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Share this with the cashier securely. They can change it later via
              <em> Forgot password</em>.
            </div>
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="bg-blue-900 hover:bg-blue-800" data-testid="cashier-submit-btn">
              {submitting ? "Adding…" : "Add cashier"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditCashierDialog({ open, onOpenChange, cashier, onUpdated }) {
  const [form, setForm] = useState(EDIT_EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open && cashier) {
      setForm({ name: cashier.name || "", phone: cashier.phone || "" });
      setError("");
    }
  }, [open, cashier]);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.patch(`/school/cashiers/${cashier.id}`, cleanPayload(form));
      toast.success("Cashier updated");
      onUpdated?.(data);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err));
    }
    setSubmitting(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-cashier-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit cashier</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Full name</Label>
            <Input value={form.name} onChange={update("name")} className="mt-1.5" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={update("phone")} className="mt-1.5" />
          </div>
          <div className="text-xs text-slate-500">
            Email cannot be changed. Use <em>Reset password</em> from the actions menu to issue new credentials.
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-blue-900 hover:bg-blue-800">
              {submitting ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ open, onOpenChange, cashier }) {
  const [pw, setPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setPw("");
      setError("");
    }
  }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/school/cashiers/${cashier.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset for ${cashier.name}`);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err));
    }
    setSubmitting(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reset-cashier-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Reset password</DialogTitle>
          <DialogDescription>
            Set a new password for this cashier. Share it securely on their next sign-in.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="text-sm text-slate-600">
            New password for <strong>{cashier?.name}</strong> ({cashier?.email})
          </div>
          <div>
            <Label>New password *</Label>
            <Input
              required
              type="text"
              minLength={8}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="mt-1.5 font-mono"
              data-testid="reset-cashier-password-input"
            />
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-blue-900 hover:bg-blue-800" data-testid="reset-cashier-submit-btn">
              {submitting ? "Resetting…" : "Reset password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Cashiers() {
  const [cashiers, setCashiers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [confirmToggle, setConfirmToggle] = useState(null);

  const load = useCallback(async (q = "", st = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("search", q);
      if (st) params.set("status", st);
      const url = `/school/cashiers${params.toString() ? `?${params.toString()}` : ""}`;
      const { data } = await api.get(url);
      setCashiers(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search, statusFilter), 300);
    return () => clearTimeout(t);
  }, [search, statusFilter, load]);

  const doToggleStatus = async (c) => {
    try {
      const { data } = await api.post(`/school/cashiers/${c.id}/toggle-status`);
      setCashiers((s) => s.map((x) => (x.id === c.id ? data : x)));
      toast.success(`${data.name} is now ${data.is_active ? "active" : "deactivated"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setConfirmToggle(null);
  };

  // Deactivating an active cashier with prior receipts asks for confirmation
  // (irreversible-feeling action: they lose login access). Reactivation is
  // one-click — no harm in switching back on.
  const onToggleClick = (c) => {
    if (c.is_active && (c.receipts_collected || 0) > 0) {
      setConfirmToggle(c);
    } else {
      doToggleStatus(c);
    }
  };

  const activeCount = cashiers.filter((c) => c.is_active).length;

  return (
    <div className="space-y-6" data-testid="cashiers-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
            Staff · Operations
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">
            Cashiers <span className="text-slate-400 font-normal">({total})</span>
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Counter staff who collect fees and run operational reports. They cannot
            see school-wide financial totals or management analytics.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          data-testid="add-cashier-btn"
          className="bg-blue-900 hover:bg-blue-800 gap-2"
        >
          <Plus size={16} weight="bold" /> Add cashier
        </Button>
      </div>

      {/* Status summary */}
      {total > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="cashiers-summary">
          <div className="p-4 border border-slate-200 bg-white rounded-lg">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Active</div>
            <div className="text-2xl font-bold text-emerald-700 font-heading mt-1">{activeCount}</div>
            <div className="text-xs text-slate-500 mt-0.5">can sign in &amp; collect</div>
          </div>
          <div className="p-4 border border-slate-200 bg-white rounded-lg">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Inactive</div>
            <div className="text-2xl font-bold text-slate-500 font-heading mt-1">{total - activeCount}</div>
            <div className="text-xs text-slate-500 mt-0.5">sign-in blocked</div>
          </div>
          <div className="p-4 border border-slate-200 bg-white rounded-lg flex items-center text-xs text-slate-500">
            <Receipt size={20} className="text-slate-300 mr-2" />
            Cashiers receive a least-privilege Operations Dashboard — no school revenue,
            no class analytics, no audit log.
          </div>
        </div>
      )}

      <div className="border border-slate-200 bg-white rounded-lg">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm min-w-[200px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by name, email, or phone…"
              className="pl-9 h-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="cashiers-search-input"
            />
          </div>
          <div className="flex gap-1.5">
            {[
              { v: "", label: "All" },
              { v: "active", label: "Active" },
              { v: "inactive", label: "Inactive" },
            ].map((f) => (
              <button
                key={f.label}
                onClick={() => setStatusFilter(f.v)}
                data-testid={`cashiers-filter-${f.label.toLowerCase()}`}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  statusFilter === f.v
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        {error && (
          <div className="px-4 py-3 text-sm text-red-600 bg-red-50 border-b border-red-200">{error}</div>
        )}

        {/* Desktop table */}
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm" data-testid="cashiers-table">
            <thead className="bg-slate-50">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left font-semibold px-4 py-3">Cashier</th>
                <th className="text-left font-semibold px-4 py-3">Email</th>
                <th className="text-left font-semibold px-4 py-3">Phone</th>
                <th className="text-right font-semibold px-4 py-3">Receipts</th>
                <th className="text-left font-semibold px-4 py-3">Status</th>
                <th className="text-right font-semibold px-4 py-3">Joined</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && cashiers.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              ) : cashiers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-slate-500" data-testid="cashiers-empty">
                    No cashiers yet. Click <strong>Add cashier</strong> to onboard your first counter staff.
                  </td>
                </tr>
              ) : (
                cashiers.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50 transition-colors" data-testid={`cashier-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center text-xs font-semibold text-amber-800">
                          {(c.name || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate">{c.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{c.email}</td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{c.phone || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-700 text-xs" data-testid={`cashier-receipts-${c.id}`}>
                      {c.receipts_collected || 0}
                    </td>
                    <td className="px-4 py-3">
                      {c.is_active ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded bg-emerald-100 text-emerald-700">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded bg-slate-200 text-slate-700">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-slate-500">
                      {new Date(c.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                            data-testid={`cashier-actions-${c.id}`}
                          >
                            <DotsThreeVertical size={18} weight="bold" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditing(c)} data-testid={`edit-cashier-${c.id}`}>
                            <PencilSimple size={14} className="mr-2" /> Edit profile
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setResetting(c)} data-testid={`reset-cashier-${c.id}`}>
                            <Key size={14} className="mr-2" /> Reset password
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onToggleClick(c)} data-testid={`toggle-cashier-${c.id}`}>
                            <Power size={14} className="mr-2" />
                            {c.is_active ? "Deactivate" : "Activate"}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-slate-100">
          {loading && cashiers.length === 0 ? (
            <div className="px-4 py-10 text-center text-slate-500">Loading…</div>
          ) : cashiers.length === 0 ? (
            <div className="px-4 py-10 text-center text-slate-500" data-testid="cashiers-empty-mobile">
              No cashiers yet. Tap <strong>Add cashier</strong> above.
            </div>
          ) : (
            cashiers.map((c) => (
              <div key={c.id} className="p-4" data-testid={`cashier-card-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 truncate">{c.name}</span>
                      {c.is_active ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-700">Active</span>
                      ) : (
                        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded bg-slate-200 text-slate-700">Inactive</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">{c.email}</div>
                    {c.phone && (
                      <div className="text-xs text-slate-500 truncate">{c.phone}</div>
                    )}
                    <div className="text-xs text-slate-500 mt-1.5">
                      <span className="font-mono font-medium text-slate-700">{c.receipts_collected || 0}</span>
                      &nbsp;receipts collected
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-1.5 rounded hover:bg-slate-100 text-slate-600">
                        <DotsThreeVertical size={18} weight="bold" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditing(c)}><PencilSimple size={14} className="mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setResetting(c)}><Key size={14} className="mr-2" /> Reset password</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onToggleClick(c)}>
                        <Power size={14} className="mr-2" /> {c.is_active ? "Deactivate" : "Activate"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <CreateCashierDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(c) => {
          setCashiers((s) => [c, ...s]);
          setTotal((n) => n + 1);
        }}
      />
      <EditCashierDialog
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        cashier={editing}
        onUpdated={(c) => setCashiers((s) => s.map((x) => (x.id === c.id ? c : x)))}
      />
      <ResetPasswordDialog
        open={!!resetting}
        onOpenChange={(v) => !v && setResetting(null)}
        cashier={resetting}
      />

      <AlertDialog open={!!confirmToggle} onOpenChange={(v) => !v && setConfirmToggle(null)}>
        <AlertDialogContent data-testid="confirm-deactivate-cashier">
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate this cashier?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{confirmToggle?.name}</strong> has collected{" "}
              <strong>{confirmToggle?.receipts_collected} receipt{(confirmToggle?.receipts_collected || 0) === 1 ? "" : "s"}</strong>{" "}
              for your school. Deactivating will block their sign-in immediately.
              Existing receipts and the audit trail remain intact, and you can
              reactivate them anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => doToggleStatus(confirmToggle)}
              className="bg-red-700 hover:bg-red-800"
              data-testid="confirm-deactivate-btn"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
